/**
 * Client-side fallback persistence for Kanban card ordering.
 *
 * WHY THIS EXISTS: the real backend's `/api/tasks` endpoint is a generic CRUD
 * resource (see `src/services/task-service.js`), not a purpose-built "tasks"
 * table with a rank/order column. We still send `order` in every
 * create/update payload (best effort — see `buildTaskPayload` in
 * `task-config.js`), but we cannot guarantee it round-trips through the real
 * backend (no test credentials were available in this environment to verify
 * — see the final report). Drag-to-reorder UX would silently "not stick" on
 * refresh if the server drops that field, which is worse than not having
 * drag-reorder at all — so ordering is ALSO mirrored into localStorage, keyed
 * by task id, and used as the authoritative sort key on load. This is a
 * pure UX enhancement layer: if it's ever cleared (or used on another
 * device/browser), cards simply fall back to priority-then-due-date sort
 * (see `sortTasksForColumn` below) — no data is lost, because status/
 * priority/etc. still live on the task object itself.
 */

import { taskId, priorityRank } from './task-config'

const STORAGE_KEY = 'taskModuleOrder.v1'

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch (e) {
    // localStorage unavailable/full — ordering just won't persist across reloads.
    console.warn('[task-order-store] could not persist order:', e)
  }
}

/** Persist the order for a single task id (0-based rank within its column). */
export function setTaskOrder(taskId, order) {
  if (taskId == null) return
  const store = readStore()
  store[String(taskId)] = order
  writeStore(store)
}

/** Persist order for a whole column in one write (used after a drag-drop). */
export function setColumnOrder(taskIdsInOrder) {
  const store = readStore()
  ;(taskIdsInOrder || []).forEach((id, index) => {
    if (id != null) store[String(id)] = index
  })
  writeStore(store)
}

export function getTaskOrder(taskId) {
  if (taskId == null) return null
  const store = readStore()
  const value = store[String(taskId)]
  return typeof value === 'number' ? value : null
}

export function clearTaskOrder(taskId) {
  if (taskId == null) return
  const store = readStore()
  delete store[String(taskId)]
  writeStore(store)
}

/**
 * Sort a column's tasks PRIORITY-FIRST (Urgent -> High -> Medium -> Low) —
 * that grouping is never crossed by drag order, so the column always reads
 * top-to-bottom by priority regardless of what's been manually reordered.
 * Explicit drag order (from localStorage) is the tie-breaker WITHIN a
 * priority group — dragging a card still reorders it freely relative to
 * its priority-mates, it just can't jump ABOVE a strictly higher-priority
 * card or BELOW a strictly lower-priority one (the same rule a physical
 * "sort by priority, then manually rank within each tier" board would
 * follow). Anything never manually reordered falls back to soonest due
 * date within its own tier. Stable, deterministic, and never loses a task.
 *
 * (Previously drag order was the ONE sort key for any task that had ever
 * been dragged, at ANY priority, with priority only used as a fallback for
 * untouched tasks — so a single low-priority card, once dragged, could
 * permanently rank above untouched Urgent cards. That's what "not actually
 * priority-wise" looked like once a column had a mix of dragged and
 * never-dragged tasks.)
 */
export function sortTasksForColumn(tasks) {
  return (tasks || [])
    .slice()
    .sort((a, b) => {
      const rankA = priorityRank(a)
      const rankB = priorityRank(b)
      if (rankA !== rankB) return rankA - rankB

      const orderA = getTaskOrder(taskId(a))
      const orderB = getTaskOrder(taskId(b))
      if (orderA != null && orderB != null && orderA !== orderB) return orderA - orderB
      if (orderA != null && orderB == null) return -1
      if (orderA == null && orderB != null) return 1

      const dueA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity
      const dueB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity
      return dueA - dueB
    })
}

export default {
  setTaskOrder,
  setColumnOrder,
  getTaskOrder,
  clearTaskOrder,
  sortTasksForColumn,
}
